import { Formik } from 'formik';
import { H2, Hidden, P, theme } from '@ahmdigital/ui';
import { map } from 'lodash/fp';
import { useRouter } from 'next/router';
import Icon from '@ahmdigital/icons-web';
import React, { useEffect, useRef, useState } from 'react';

import {
  BackButton,
  CloseButton,
  HeadingWrapper,
  LabelSpacer,
  MenuWrapper,
  MobileButton,
  MobileScrollWrapper,
  MobileToggle,
  MobileToggleWrapper,
  OfferTongueWrapper,
  StyledH2,
  StyledP,
  WidgetWrapper,
} from './styles';
import { FormValues } from './types';
import { SectionOfferStrip } from '../../../../../insurance/types';
import browser from '../../../../../../utils/browser';
import device from '../../../../../../utils/device';
import fieldData from './data';
import generateSchema from './schema';
import handleBackClick from './utils/handle-click-back';
import handleSubmit from './utils/handle-submit';
import OfferTongue from '../offer-tongue';
import Overlay from './components/overlay';
import uncapFpIterator from '../../../../../../../utils/uncap-fp-iterator';
import useTravelWidgetContext from './hooks/use-travel-widget-context';
import WidgetForm from './components/widget-form';

const { colors } = theme;
const mapWithKey = uncapFpIterator(map);

type TravelWidgetProps = {
  isDestinationPage?: boolean;
  isWidgetActive: boolean;
  offer: SectionOfferStrip;
  setIsWidgetActive: (value: boolean) => void;
};

const TravelWidget = ({ isWidgetActive, offer, setIsWidgetActive, isDestinationPage }: TravelWidgetProps) => {
  const { countries, currentWidgetErrors, travellerCount } = useTravelWidgetContext();
  const { packagedSchema } = generateSchema({ countries, currentWidgetErrors, travellerCount });
  const router = useRouter();

  const [modalStates, setModalStates] = useState(map(() => false, fieldData));
  const [currentModalIndex, setCurrentModalIndex] = useState<number | null>(null);
  const widgetWrapperRef = useRef<HTMLDivElement>(null);

  const toggleModal = (modalPosition: number, value: boolean) => {
    setModalStates((modalState) =>
      mapWithKey((_state: number, index: number) => (index === modalPosition ? value : false), modalState),
    );
    setCurrentModalIndex(value ? modalPosition : null);
  };

  const closeAllModals = () => {
    setModalStates((modalState) => mapWithKey(() => false, modalState));
  };

  const setWidgetInactive = () => {
    setIsWidgetActive(false);
    closeAllModals();
  };

  const documentObj = browser.getDocument();

  useEffect(() => {
    if (isWidgetActive && device.isMobile()) {
      documentObj.body.style.overflow = 'hidden';
    }

    return () => {
      documentObj.body.style.overflow = 'auto';
    };
  }, [isWidgetActive]);

  useEffect(() => {
    if (isWidgetActive) {
      widgetWrapperRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [isWidgetActive]);

  const TAB_KEY = 'Tab';
  // when tabbing back off the widget
  const handleCloseWidgetOnShiftTab = (event: React.KeyboardEvent<HTMLButtonElement>) => {
    if (event.key === TAB_KEY && event.shiftKey) {
      setWidgetInactive();
    }
  };
  // when tabbing forward off the widget
  const handleCloseWidgetOnTab = (event: React.KeyboardEvent<HTMLButtonElement>) => {
    if (event.key === TAB_KEY && !event.shiftKey) {
      setWidgetInactive();
    }
  };

  return (
    <>
      <Hidden df xl lg>
        <MobileToggleWrapper displayMobileToggle={!isWidgetActive} isDestinationPage={isDestinationPage}>
          <OfferTongueWrapper displayOffer={!isWidgetActive}>
            <OfferTongue offer={offer} />
          </OfferTongueWrapper>
          <MobileToggle displayMobileToggle={!isWidgetActive} onClick={() => setIsWidgetActive(true)} isUnstyled>
            <HeadingWrapper>
              <StyledH2 looksLike="h5">Get a quote</StyledH2>
              <StyledP>Where, When, Who</StyledP>
            </HeadingWrapper>
            <MobileButton>
              <Icon name="arrow-right" fill={colors.white} size="large" />
            </MobileButton>
          </MobileToggle>
        </MobileToggleWrapper>
      </Hidden>

      <Hidden xs sm md>
        <Overlay isShowing={isWidgetActive} onClick={setWidgetInactive} />
      </Hidden>
      <WidgetWrapper
        displayWidget={isWidgetActive}
        isActive={isWidgetActive}
        ref={widgetWrapperRef}
        isDestinationPage={isDestinationPage}
      >
        <MenuWrapper>
          {isWidgetActive && (
            <CloseButton aria-label="Close quote" onClick={setWidgetInactive} onKeyDown={handleCloseWidgetOnShiftTab}>
              <Icon name="cross-light" fill={colors.black} />
            </CloseButton>
          )}
          <Hidden xs sm md>
            <OfferTongueWrapper displayOffer={!isWidgetActive}>
              <OfferTongue offer={offer} />
            </OfferTongueWrapper>
          </Hidden>
          <H2 looksLike="h4">Get a travel quote</H2>
          {isWidgetActive && (
            <BackButton onClick={() => handleBackClick({ currentModalIndex, setWidgetInactive, toggleModal })}>
              <Icon name="app-chevron-left" fill={colors.black} />
            </BackButton>
          )}
        </MenuWrapper>

        <LabelSpacer>
          <P>All fields marked with * are required</P>
        </LabelSpacer>
        <MobileScrollWrapper>
          <Formik<FormValues>
            {...packagedSchema}
            onSubmit={(formValues: FormValues) => handleSubmit(formValues, router)}
          >
            <WidgetForm
              handleCloseWidgetOnTab={handleCloseWidgetOnTab}
              modalStates={modalStates}
              toggleModal={toggleModal}
              setIsWidgetActive={setIsWidgetActive}
            />
          </Formik>
        </MobileScrollWrapper>
      </WidgetWrapper>
    </>
  );
};
export default TravelWidget;
